/* ⚠️ GENERATED from app/services/mediaRuns.ts by gen-twins.mjs — edit the TypeScript, not this. */
/* services/mediaRuns.ts — photos, videos and documents sent one at a time, shown together.

   "If I send single images and videos within the same few minutes, group them —
    but each one keeps its own forward, its own delete, its own ticks. The merge
    happens when I leave the chat, not while I am in it. Same in groups, and for
    documents too."
   "…but not a photo now and a video half an hour later, and not ones that have
    not been seen yet. Smarter — convenient for the sender and the receiver."

   🟢 A RUN IS A WAY OF DRAWING, NOT A WAY OF STORING. Nothing here touches a
      message: each photo stays the message it was — its own id, receipts, star,
      reactions, delete scope, forward. A run is a row the LIST builds out of
      neighbouring messages, the way a day badge is. So there is nothing to undo,
      nothing to migrate, nothing to sync, and nothing the other side has to agree
      with: two phones may group differently and neither is wrong.

   THE RULES — every one has to hold:

   1. SENT TOGETHER. Neighbours in the chat, same person, same kind (photos and
      videos together; documents together), each within RUN_GAP_MS of the one
      before AND the whole run within RUN_SPAN_MS of its first item, on one day.
      A photo now and a video half an hour later are two moments, not one.

   2. NOTHING OF ITS OWN TO SAY. A caption, a reply quote, a forward, a
      reaction, a disappearing timer, a failed download / dead key, something
      still sending, a deleted or cleared stub, a sticker — each of those is read
      as itself and stays a bubble.

   3. ALREADY SEEN — by whoever it matters to.
        · Something sent TO me joins a run only once I have seen it: whatever was
          still unread when I opened the chat stays single for this visit, so
          new photos are met one by one.
        · Something I SENT joins once the other side has seen it (read), or a day
          has passed (read receipts can be off) — until then each has its own
          ticks in plain sight, and delete-for-everyone is one press away.

   4. FROZEN FOR THE VISIT. A run only takes what was already in the chat when it
      was opened (RunVisit), and whether an item may join is decided when it is
      first admitted — a reaction arriving, a timer arming, a read receipt landing
      while you read do not fold or unfold anything under your finger. Only a
      delete (the item is no longer a photo) takes it out mid-visit. The next
      visit asks again.

   5. ONCE GROUPED, STAYS GROUPED. 🟥 "They group, then on another entry two of
      them come apart, then the next entry they are back." Rules 2 and 3 are read
      off the copy of a message the chat draws FIRST — the light preview row the
      chat list hands over — and whether that copy already carries its read
      receipt, its seq, its reaction, depends on what synced before the tap. So
      the same photo could be judged differently on two entries. A run is a
      promise to the reader: what was drawn together stays together. Every item
      drawn inside a run is remembered (`settled`, kept by the caller across
      visits and restarts) and joins again on every later visit without being
      re-judged — only the structural rule (deleted, cleared, a caption) and the
      timing (rule 1) still apply, and those cannot flicker.

   Album items — the other half of the request — live at the bottom: an album is
   ONE message holding several media, so "delete one photo of it" is a field on
   that message (removed for everyone / hidden for me), never a rewrite of its
   arrays. Every index stays the index it was uploaded under.

   Pure: no React, no Firebase. The browser runs the same file (tools/gen-twins). */

/* ⏱ "Even if I forward single images or videos, or photos and videos one after another, within a 15-minute
   window — reuse the same logic and group them." The window was 2 minutes between neighbours and 10 for the
   whole run; it is the 15 minutes asked for, for both. */
/** consecutive items further apart than this (by send time) start a new run */
export const RUN_GAP_MS = 15 * 60 * 1000;
/** …and a run never spans more than this from its first item to its last */
export const RUN_SPAN_MS = 15 * 60 * 1000;
/** fewer than this many in a row are left as they are */
export const RUN_MIN = 2;
/** a run never grows past this — the rest start the next one */
export const RUN_MAX = 30;
/** my own items join without a read receipt once they are this old */
export const SEEN_FALLBACK_MS = 24 * 60 * 60 * 1000;
/** the row type the chat list draws */
export const RUN_TYPE = 'media-run';
const RUN_PREFIX = 'run:';
/** milliseconds from any timestamp shape a message carries */
export function msOf(t) {
  if (t == null) return 0;
  if (typeof t === 'number') return Number.isFinite(t) ? t : 0;
  if (typeof t === 'string') {
    const n = Date.parse(t);
    return Number.isFinite(n) ? n : 0;
  }
  if (t instanceof Date) return t.getTime();
  if (typeof t.toMillis === 'function') {
    try {
      return t.toMillis();
    } catch {
      return 0;
    }
  }
  if (typeof t.toDate === 'function') {
    try {
      return t.toDate().getTime();
    } catch {
      return 0;
    }
  }
  if (typeof t.seconds === 'number') return t.seconds * 1000;
  return 0;
}

/** when THIS device first had the message (the list's own order key), else its send time */
export function arrivalMs(m) {
  if (m && m._ord != null && Number.isFinite(Number(m._ord))) return Number(m._ord);
  return msOf(m && m.timestamp);
}
const hasWords = v => typeof v === 'string' && v.trim().length > 0;
const hasAny = v => Array.isArray(v) ? v.length > 0 : !!(v && typeof v === 'object' && Object.keys(v).length > 0);
/** a reaction somebody actually holds — `{ '❤️': [] }` is a reaction taken back, not one */
export function hasReaction(r) {
  if (Array.isArray(r)) return r.length > 0;
  if (!r || typeof r !== 'object') return false;
  return Object.values(r).some(u => Array.isArray(u) ? u.length > 0 : !!u);
}

/** Rule 2, the part that can never change while you look: what kind of thing it is.
    A deleted / cleared / unsent item fails this at any time — even mid-visit. */
export function baseKindOf(m) {
  if (!m || typeof m !== 'object') return null;
  const t = m.type;
  if (t !== 'image' && t !== 'video' && t !== 'document') return null;
  const id = String(m.id ?? '');
  if (!id || id.startsWith('temp-') || id.startsWith(RUN_PREFIX)) return null;
  if (m.isDeleted || m.deleted) return null;
  if (m.isSticker || m.isGif || m.viewOnce) return null;
  if (m.systemType) return null;
  if (readBatch(m.batch)) return null; // 📦 picked together: drawn as its batch (below), never guessed into a run
  if (m.replyTo) return null;
  if (hasWords(m.caption)) return null;
  if (m.mediaDeletedFromStorage) return null;
  if (m.isSending || m.sent === false || m.uploadStatus === 'failed' || m._uploadCancelled) return null;
  if (m._pending || m._failed) return null; // the browser's own in-flight rows
  return t === 'document' ? 'doc' : 'visual';
}

/** Rule 2, the part that CAN change while you look — decided once per visit (rule 4). */
export function softBlocked(m) {
  if (!m) return true;
  /* (a FORWARD joins too now — "group forwarded ones within 15 minutes, docs as well": each tile and each
     file still says it was forwarded — the ↪ on its corner, on the doc pager's line) */
  if (hasReaction(m.reactions)) return true; // its reactions stay in sight
  if (m.expiresAt || m.disappearAfter) return true; // it will go on its own
  if (m.mediaDecryptDead || m.imageDownloadFailed || m.videoDownloadFailed) return true; // needs its own retry
  return false;
}

/** May this message join a run at all, right now? (Rules 2 — both halves.) */
export function runKindOf(m) {
  const k = baseKindOf(m);
  return k && !softBlocked(m) ? k : null;
}

/** Rule 1 between two neighbours (`kindOf` lets a visit supply its frozen answer). */
export function canJoin(newer, older, kindOf = runKindOf) {
  const a = kindOf(newer);
  const b = kindOf(older);
  if (!a || a !== b) return false;
  if (String(newer.senderId || '') !== String(older.senderId || '') || !newer.senderId) return false;
  const tn = msOf(newer.timestamp);
  const to = msOf(older.timestamp);
  if (!tn || !to) return false;
  /* never across midnight — a run sits under ONE day label (the phone's list has a
     badge between days anyway; the browser draws its separators after grouping) */
  if (new Date(tn).toDateString() !== new Date(to).toDateString()) return false;
  return Math.abs(tn - to) <= RUN_GAP_MS;
}
export function isRun(m) {
  return !!(m && m.type === RUN_TYPE && Array.isArray(m.items));
}
export function runItems(m) {
  return isRun(m) ? m.items : [];
}

/** Does this list row stand for message `id` — itself, or as one item of a run? */
export function rowHasId(row, id) {
  if (!row || id == null) return false;
  const s = String(id);
  if (String(row.id) === s) return true;
  if (!isRun(row)) return false;
  return row.items.some(m => m && (String(m.id) === s || m.clientTempId && String(m.clientTempId) === s));
}

/** The keys to remember for every item drawn inside a run (rule 5): its id, and its
    client id too — a row can come back under either. */
export function runSettleKeys(rows) {
  const out = [];
  for (const r of rows || []) {
    if (!isRun(r)) continue;
    for (const m of r.items) {
      if (!m) continue;
      if (m.id != null) out.push(String(m.id));
      if (m.clientTempId) out.push(String(m.clientTempId));
    }
  }
  return out;
}

/** Does a remembered key set name this message (by id or client id)? */
export function isSettledIn(keys, m) {
  if (!keys || !m) return false;
  return keys.has(String(m.id)) || !!m.clientTempId && keys.has(String(m.clientTempId));
}

/** Every message a set of rows stands for (a run contributes its items). */
export function rowMessages(rows) {
  const out = [];
  for (const r of rows || []) {
    if (isRun(r)) out.push(...r.items);else if (r) out.push(r);
  }
  return out;
}

/* ─── the visit: what was already here, and already seen, when the chat opened ──
   🟥 "ARRIVED BEFORE THE CHAT OPENED" CANNOT BE READ OFF A TIMESTAMP. A backlog
      that lands a second after opening (five photos sent an hour ago, delivered
      now) carries an hour-old key — by the clock it is history. So the visit keeps
      the ids it STARTED with: everything the chat drew while its own stored history
      was loading (the seed, then the disk window), plus anything older than all of
      it that scrolling pages in later. Whatever else appears — a send, a delivery,
      a catch-up — stays single until the next visit.
   🟥 AND THE BASELINE IS THERE FROM THE FIRST FRAME. Admitting nothing until the
      history loaded would draw every photo single and then fold them a moment
      later — a flicker on every entry. While the chat is still loading, every row
      it draws is admitted (it is, by definition, what was here when it opened);
      `historyReady` only closes the door.
   🟢 RULE 3 IS DECIDED HERE TOO, and frozen with it: what was unread when the chat
      opened (the newest `unreadAtOpen` messages from others), and which of my own
      had been seen — so a read receipt landing mid-visit folds nothing. */

export function newRunVisit(opts = {}) {
  return {
    ready: false,
    known: new Set(),
    oldestMs: Infinity,
    ok: new Map(),
    unseen: new Set(),
    myUid: String(opts.myUid || ''),
    unreadAtOpen: Math.max(0, Number(opts.unreadAtOpen) || 0),
    seenByOthers: typeof opts.seenByOthers === 'function' ? opts.seenByOthers : () => false,
    settled: typeof opts.settled === 'function' ? opts.settled : () => false,
    now: Number(opts.now) || Date.now()
  };
}
const NOT_A_MESSAGE = new Set(['day-badge', 'unread-divider', 'import-skeleton', RUN_TYPE, 'system']);
const messageRowsOf = rows => (rows || []).filter(m => m && m.id != null && !NOT_A_MESSAGE.has(m.type));

/** The newest `count` messages from others in a newest-first list — what was unread. */
export function unseenAtOpen(rowsNewestFirst, myUid, count) {
  const out = new Set();
  if (!count || count <= 0) return out;
  for (const m of messageRowsOf(rowsNewestFirst)) {
    if (!m.senderId || String(m.senderId) === String(myUid)) continue;
    if (m.systemType === 'call_history') continue;
    out.add(String(m.id));
    if (out.size >= count) break;
  }
  return out;
}

/** Rule 3 + rule 2's soft half, for one message, at the moment it is admitted. */
function mayJoin(visit, m) {
  if (visit.settled(m)) return true; // rule 5: grouped before → grouped again
  if (softBlocked(m)) return false;
  const id = String(m.id);
  const mine = !!visit.myUid && String(m.senderId || '') === visit.myUid;
  if (!mine) return !visit.unseen.has(id);
  if (visit.seenByOthers(m)) return true;
  const t = msOf(m.timestamp);
  return !!t && visit.now - t >= SEEN_FALLBACK_MS;
}

/**
 * Tell the visit what the list holds now (newest first). Until `historyReady`
 * every row is admitted and judged afresh; from then on the baseline is frozen and
 * only rows OLDER than everything admitted (history paging in) are added.
 * Mutates and returns the visit.
 */
export function admitRows(visit, rows, historyReady) {
  if (!visit) return visit;
  const ms = messageRowsOf(rows);
  if (!visit.ready) {
    visit.unseen = unseenAtOpen(ms, visit.myUid, visit.unreadAtOpen);
    for (const m of ms) {
      const id = String(m.id);
      /* judged ONCE, the first time it is drawn — the seed's copy and the disk's
         copy of the same message must not fold it one frame and unfold it the next */
      if (!visit.known.has(id)) {
        visit.known.add(id);
        visit.ok.set(id, mayJoin(visit, m));
      }
      const t = msOf(m.timestamp);
      if (t && t < visit.oldestMs) visit.oldestMs = t;
    }
    if (historyReady) visit.ready = true;
    return visit;
  }
  let oldest = visit.oldestMs;
  for (const m of ms) {
    const id = String(m.id);
    if (visit.known.has(id)) continue;
    const t = msOf(m.timestamp);
    if (t && t < visit.oldestMs) {
      visit.known.add(id);
      visit.ok.set(id, mayJoin(visit, m));
      if (t < oldest) oldest = t;
    }
  }
  visit.oldestMs = oldest;
  return visit;
}

/** May this message be grouped during this visit? */
export function admittedInVisit(visit, m) {
  if (!visit) return true;
  const id = String(m && m.id);
  return visit.known.has(id) && visit.ok.get(id) === true;
}

/** The visit's kind for a message: the structural rule live (a delete still takes it
    out), the rest as decided when it was admitted. */
export function runKindInVisit(visit, m) {
  const k = baseKindOf(m);
  if (!k) return null;
  if (!visit) return softBlocked(m) ? null : k;
  return admittedInVisit(visit, m) ? k : null;
}
const sameItems = (a, b) => a.length === b.length && a.every((x, i) => x === b[i]);

/* 🟥 A RUN'S KEY MUST OUTLIVE ITS NEWEST ITEM. The row is keyed by its newest item —
      so deleting that one photo renamed the row, and the list threw the whole
      bubble away and mounted a new one (every tile blank for a frame). The cache
      remembers which run each item was drawn in; a run that still holds any of
      them keeps that key (the first one claimed, if a delete split it in two). */
const ITEM_KEY = 'item:';
function makeRun(segNewestFirst, kind, cache, used) {
  const newest = segNewestFirst[0];
  const items = segNewestFirst.slice().reverse(); // oldest → newest, the order they were sent
  let id = `${RUN_PREFIX}${newest.id}`;
  if (cache) {
    for (const it of segNewestFirst) {
      const was = cache.get(`${ITEM_KEY}${it.id}`);
      if (typeof was === 'string' && !(used && used.has(was))) {
        id = was;
        break;
      }
    }
  }
  if (used) used.add(id);
  if (cache) for (const it of items) cache.set(`${ITEM_KEY}${it.id}`, id);
  const prev = cache && cache.get(id);
  if (prev && Array.isArray(prev.items) && sameItems(prev.items, items)) return prev;
  const row = {
    id,
    type: RUN_TYPE,
    runKind: kind,
    items,
    senderId: newest.senderId,
    senderName: newest.senderName,
    senderImage: newest.senderImage,
    timestamp: newest.timestamp,
    _ord: newest._ord,
    seq: newest.seq
  };
  if (cache) cache.set(id, row);
  return row;
}

/* ═══ A BATCH — photos and videos picked together, each one sent on its own ═════════
   "I send 10 items… one image finished uploading gets sent immediately, even the video…
    once they are fully sent they go back to be grouped." — and, asked which: GROUPED FROM
    THE START, nothing regroups.
   🟥 An album was ONE message: nothing of it reached anybody until the last of its files
      had uploaded, and one slow video held nine finished photos back.
   🟢 Each item is its own message now — its own upload, document, push, ticks, delete,
      reply, forward — sent the moment its own bytes are up. What ties them is a tag the
      SENDER stamps on every one of them at the tap: `batch: { id, i, n }` (the pick, the
      item's place in it, how many were picked). The list draws all of one sender's items
      with one tag as ONE row, from the first frame — on the sender's phone the instant
      Send is pressed, on a receiver's the instant the first item lands — at the place of
      the oldest of them, tiles in the order they were picked, and a WAITING slot for each
      one still on its way. Its key is the batch, its size the number picked: an arrival
      fills a slot, it never reshapes the bubble or moves a page under a finger.
   Rules 3–5 do not apply — a batch is a promise the sender made, not a guess this phone
   makes. Rule 2 shrinks to what the item is: a deleted one is drawn as its own stub (its
   slot answered, not waited for); a caption is the batch's (item 0 carries it). A slot
   is waited for only while the batch is fresh (BATCH_WAIT_MS since its newest arrival)
   and not complete (the sender marks the LAST item it writes, and every item written
   after one was cancelled carries the smaller count) — an item the sender gave up on
   must not leave a spinner behind for ever. */

/** the most one pick can hold */
export const BATCH_MAX = 30;
/** an empty slot is waited for this long after the batch's newest arrival */
export const BATCH_WAIT_MS = 30 * 60 * 1000;
/* 📐 the pick's shapes, one letter per item in pick order (p upright, l sideways, s square-ish), stamped by
   the sender at the tap — the receiver lays the bubble out from the first item that lands (services/mediaLayout) */
const SHAPES_RE = /^[pls]{1,30}$/;
const BATCH_ID = /^[A-Za-z0-9_-]{4,48}$/;
const BATCH_PREFIX = 'run:batch:';

/** The tag, from a message field or a push (FCM data is strings: "id.i.n"). null when not one. */
export function readBatch(v) {
  let o = v;
  if (typeof v === 'string') {
    const p = v.split('.');
    if (p.length !== 3 && !(p.length === 4 && p[3] === 'l')) return null;
    o = {
      id: p[0],
      i: Number(p[1]),
      n: Number(p[2]),
      last: p.length === 4
    };
  }
  if (!o || typeof o !== 'object') return null;
  const id = String(o.id || '');
  const i = Number(o.i);
  const n = Number(o.n);
  /* n can drop below an item's index: it is how many are still coming, and a cancelled item is not */
  if (!BATCH_ID.test(id) || !Number.isInteger(i) || !Number.isInteger(n) || i < 0 || i >= BATCH_MAX || n < 1 || n > BATCH_MAX) return null;
  const shape = typeof o.o === 'string' && SHAPES_RE.test(o.o) ? {
    o: o.o
  } : {};
  return o.last === true ? {
    id,
    i,
    n,
    last: true,
    ...shape
  } : {
    id,
    i,
    n,
    ...shape
  };
}

/** The tag as one string, for a push's data map ("id.i.n", "id.i.n.l" for the last one written). */
export function batchWire(v) {
  const b = readBatch(v);
  return b ? `${b.id}.${b.i}.${b.n}${b.last ? '.l' : ''}` : '';
}

/** 📦 A push's pick, as the Android notifier keys its one updating line: "<from>|<id>" (android BatchLines.kt). */
export function batchLineKey(data) {
  const b = data ? readBatch(data.batch) : null;
  const from = data ? String(data.fromUserId || data.senderId || '') : '';
  return b && from ? `${from}|${b.id}` : null;
}

/** 📦 A push's line for a pick so far ("📷 3 photos") — the key and params of the notification strings, or
    null when one item speaks for itself. The server's twin is server/functions/batchPush.js batchBodyKey. */
export function batchLine(data) {
  if (!data || !readBatch(data.batch)) return null;
  const m = typeof data.batchSum === 'string' ? /^(\d{1,2})\.(\d{1,2})$/.exec(data.batchSum.trim()) : null;
  if (!m) return null;
  const p = Number(m[1]);
  const v = Number(m[2]);
  if (p + v < 2) return null;
  if (v === 0) return {
    key: 'n_photos',
    params: {
      count: p
    }
  };
  if (p === 0) return {
    key: 'n_videos',
    params: {
      count: v
    }
  };
  return {
    key: 'n_media',
    params: {
      count: p + v
    }
  };
}

/** A fresh batch id (time + randomness; one per pick). */
export function makeBatchId(now = Date.now(), rand = Math.random) {
  return `b${Math.max(0, Math.floor(now)).toString(36)}${Math.floor(rand() * 1e9).toString(36)}`.slice(0, 24);
}
const isVisualType = m => !!m && (m.type === 'image' || m.type === 'video') && !m.isSticker && !m.isGif && !m.viewOnce && !m.systemType;
const isGoneItem = m => !!m && (m.isDeleted || m.deleted || m.mediaDeletedFromStorage);

/** "which batch is this message part of" — sender + tag; null when it is not part of one */
export function batchKeyOf(m) {
  if (!m || !m.senderId) return null;
  const b = readBatch(m.batch);
  return b ? `${m.senderId}|${b.id}` : null;
}

/** How far an item's send has got, for the batch's own ticks (the least of them is the batch's) —
    the order the tick model reads (stores/messageStatus): failed, on its way, sent, delivered, read. */
export function batchRank(m) {
  if (!m) return 0;
  if (m.uploadStatus === 'failed' || m.sendFailed || m._uploadCancelled) return 0;
  if (m.isSending || m.sent === false || String(m.id || '').startsWith('temp-')) return 1;
  if (m.seen) return 4;
  if (m.delivered) return 3;
  return 2;
}

/** The batch's item whose ticks speak for all of it: the least advanced. */
export function leastItem(items, rank = batchRank) {
  let best = null;
  let br = Infinity;
  for (const it of items || []) {
    const r = rank(it);
    if (r < br) {
      br = r;
      best = it;
    }
  }
  return best;
}
/** One pass over the list: every batch, its live items and where they sit. */
function gatherBatches(list) {
  let groups = null;
  for (let k = 0; k < list.length; k += 1) {
    const m = list[k];
    const key = batchKeyOf(m);
    if (!key) continue;
    const b = readBatch(m.batch);
    if (!groups) groups = new Map();
    let g = groups.get(key);
    if (!g) {
      g = {
        key,
        tagId: b.id,
        idx: [],
        items: [],
        answered: new Set(),
        minN: b.n,
        newest: 0,
        complete: false,
        shapes: ''
      };
      groups.set(key, g);
    }
    if (!g.shapes && b.o) g.shapes = b.o; // 📐 every item carries the pick's shapes; the first that has them says
    g.answered.add(b.i);
    if (b.n < g.minN) g.minN = b.n;
    if (b.last) g.complete = true; // the sender wrote its last one: nothing more is coming
    if (isGoneItem(m) || !isVisualType(m)) continue; // drawn as itself; its slot is not waited for
    g.idx.push(k);
    g.items.push(m);
    const a = arrivalMs(m);
    if (a > g.newest) g.newest = a;
  }
  return groups;
}

/** The row a batch is drawn as (identity kept across rebuilds while nothing in it changed). */
function makeBatchRow(g, now, cache) {
  const items = g.items.slice().sort((a, b) => readBatch(a.batch).i - readBatch(b.batch).i || arrivalMs(a) - arrivalMs(b));
  const fresh = g.newest > 0 && now - g.newest < BATCH_WAIT_MS;
  const waiting = fresh && !g.complete ? Math.max(0, g.minN - g.answered.size) : 0;
  /* the slots in the order they were picked: an item where it has landed, a wait where one is still coming */
  const slots = items.map(it => ({
    i: readBatch(it.batch).i,
    id: String(it.id)
  }));
  for (let i = 0, left = waiting; left > 0 && i < BATCH_MAX; i += 1) {
    if (g.answered.has(i)) continue;
    slots.push({
      i,
      id: null
    });
    left -= 1;
  }
  slots.sort((a, b) => a.i - b.i);
  const newest = items.reduce((a, b) => msOf(b.timestamp) > msOf(a.timestamp) ? b : a, items[0]);
  /* the pick's caption rides on its first item (the browser opens a sealed one into `_caption`) */
  const capOf = it => hasWords(it.caption) ? String(it.caption) : hasWords(it._caption) ? String(it._caption) : '';
  const capItem = items.find(it => !!capOf(it));
  const caption = capItem ? capOf(capItem) : '';
  const tickOf = leastItem(items);
  const id = `${BATCH_PREFIX}${g.key}`;
  const sig = slots.map(s => s.id || '_').join(',');
  const prev = cache && cache.get(id);
  if (prev && prev.batch && prev.batch.sig === sig && prev.batch.o === g.shapes && prev.caption === caption && prev._tickOf === tickOf && sameItems(prev.items, items)) return prev;
  const row = {
    id,
    type: RUN_TYPE,
    runKind: 'visual',
    items,
    batch: {
      id: g.tagId,
      n: slots.length,
      waiting,
      slots,
      sig,
      o: g.shapes
    },
    caption,
    _tickOf: tickOf,
    senderId: newest.senderId,
    senderName: newest.senderName,
    senderImage: newest.senderImage,
    timestamp: newest.timestamp,
    _ord: newest._ord,
    seq: newest.seq
  };
  if (cache) cache.set(id, row);
  return row;
}
export function isBatchRun(m) {
  return isRun(m) && !!m.batch && Array.isArray(m.batch.slots);
}

/** 👆 The item a run answers as when it is acted on as ONE (a reaction from its peek, its reply quote): a pick's
    first item (the one picked first), a run's newest. */
export function runLead(run) {
  const its = isRun(run) ? run.items.filter(Boolean) : [];
  if (!its.length) return null;
  return isBatchRun(run) ? its[0] : its[its.length - 1];
}

/** 📦 A page that stands for an item still on its way (the viewer draws it as a wait). */
export function isWaitingPage(m) {
  return !!(m && m._waiting === true);
}

/** 📦 A batch row as the viewer's pages: every slot, in the order picked — a landed item as itself,
    one still coming as a waiting page that keeps its place (so an arrival never moves the page you are on). */
export function batchPages(row) {
  if (!isBatchRun(row)) return [];
  const byId = new Map((row.items || []).map(it => [String(it.id), it]));
  return row.batch.slots.map(sl => sl && sl.id != null && byId.get(String(sl.id)) || {
    id: `wait:${row.id}:${sl.i}`,
    _waiting: true,
    type: 'image',
    senderId: row.senderId,
    batch: {
      id: row.batch.id,
      i: sl.i
    }
  });
}

/** 📦 The pages of the batch `rowId` names, read again from the chat's messages as they are now. */
export function batchPagesIn(messages, rowId, now) {
  const key = String(rowId || '').startsWith(BATCH_PREFIX) ? String(rowId).slice(BATCH_PREFIX.length) : '';
  if (!key) return [];
  const mates = (messages || []).filter(m => batchKeyOf(m) === key);
  const g = gatherBatches(mates);
  const grp = g && g.get(key);
  if (!grp || !grp.items.length) return [];
  return batchPages(makeBatchRow(grp, Number(now) > 0 ? Number(now) : Date.now()));
}

/** 📦 The slot a page stands for (its place in the pick), or -1. */
export function pageSlot(m) {
  const b = m && (m._waiting ? m.batch : readBatch(m.batch));
  return b && Number.isInteger(Number(b.i)) ? Number(b.i) : -1;
}

/**
 * The list with runs folded in. `list` is the chat's render order — NEWEST FIRST
 * (the inverted FlatList's data), day badges included; a badge or any other row
 * between two media messages ends a run, so a run never crosses midnight.
 * Returns the input itself when there is nothing to fold.
 */
export function buildMediaRuns(list, opts = {}) {
  if (!Array.isArray(list) || list.length < 1) return list;
  const admit = typeof opts.admit === 'function' ? opts.admit : () => true;
  const base = typeof opts.kindOf === 'function' ? opts.kindOf : runKindOf;
  const kindOf = m => admit(m) ? base(m) : null;
  let out = null;
  const used = new Set();
  /* 📦 the batches first: where each is drawn (its oldest item's place) and which rows it swallows */
  const groups = gatherBatches(list);
  let batchAt = null; // where a batch is drawn → its row
  let inBatch = null; // every row a batch swallows
  if (groups) {
    const now = Number.isFinite(Number(opts.now)) && Number(opts.now) > 0 ? Number(opts.now) : Date.now();
    for (const g of groups.values()) {
      if (!g.items.length || g.items.length < 2 && g.minN < 2) continue; // a pick of one is a photo
      if (!batchAt || !inBatch) {
        batchAt = new Map();
        inBatch = new Set();
      }
      batchAt.set(Math.max(...g.idx), makeBatchRow(g, now, opts.cache));
      for (const k of g.idx) inBatch.add(k);
    }
  }
  if (!batchAt && list.length < RUN_MIN) return list;
  let i = 0;
  while (i < list.length) {
    const m = list[i];
    if (inBatch && inBatch.has(i)) {
      if (!out) out = list.slice(0, i);
      const row = batchAt && batchAt.get(i);
      if (row) out.push(row);
      i += 1;
      continue;
    }
    if (!kindOf(m)) {
      if (out) out.push(m);
      i += 1;
      continue;
    }
    /* rule 1: each neighbour close to the last, the whole run close to its newest */
    const t0 = msOf(m.timestamp);
    let j = i + 1;
    while (j < list.length && canJoin(list[j - 1], list[j], kindOf) && Math.abs(t0 - msOf(list[j].timestamp)) <= RUN_SPAN_MS) j += 1;
    const seg = list.slice(i, j);
    if (seg.length >= RUN_MIN) {
      if (!out) out = list.slice(0, i);
      for (let k = 0; k < seg.length; k += RUN_MAX) {
        const chunk = seg.slice(k, k + RUN_MAX);
        if (chunk.length >= RUN_MIN) out.push(makeRun(chunk, kindOf(chunk[0]), opts.cache, used));else out.push(...chunk);
      }
    } else if (out) {
      out.push(...seg);
    }
    i = j;
  }
  return out || list;
}

/* ─── the chat list: the run the newest message is part of ────────────────
   "When single photos and videos got grouped, the chat list's last message should
    show them grouped too — '2 Photos, 1 Video' with their thumbnails, max 3."
   🟢 ONLY WHAT THE CHAT ITSELF DREW TOGETHER. The list does not run the visit's
      rules again (it has no visit); it asks rule 5's memory — `isSettled`, the
      items drawn inside a run — and rule 1 (still neighbours in time, same sender,
      same kind, one day) on the rows it holds. So the row and the chat cannot
      disagree: unread photos the chat will show one by one are one by one here. */
export function previewRun(rowsNewestFirst, isSettled) {
  const rows = (rowsNewestFirst || []).filter(m => m && m.type !== 'day-badge');
  const start = rows.findIndex(m => !m.isDeleted);
  if (start < 0) return null;
  const kindOf = m => isSettled(m) ? baseKindOf(m) : null;
  const first = rows[start];
  /* 📦 the newest is part of a batch: the batch is what the chat draws, so it is what the line says
     ("📷 3 Photos" while the rest are still on their way, then "📷 10 Photos") */
  const bk = batchKeyOf(first);
  if (bk && isVisualType(first)) {
    const mates = rows.slice(start, start + 4 * BATCH_MAX).filter(m => batchKeyOf(m) === bk && isVisualType(m) && !isGoneItem(m));
    if (mates.length >= RUN_MIN) return mates.sort((a, b) => readBatch(a.batch).i - readBatch(b.batch).i);
    return null;
  }
  if (kindOf(first) !== 'visual') return null;
  const t0 = msOf(first.timestamp);
  let j = start + 1;
  while (j < rows.length && j - start < RUN_MAX && canJoin(rows[j - 1], rows[j], kindOf) && Math.abs(t0 - msOf(rows[j].timestamp)) <= RUN_SPAN_MS) j += 1;
  const seg = rows.slice(start, j);
  return seg.length >= RUN_MIN ? seg.reverse() : null; // oldest → newest, as the grid reads
}

/** "2 Photos, 1 Video" counts for a set of messages / album urls */
export function visualCounts(items) {
  let photos = 0;
  let videos = 0;
  for (const m of items || []) {
    if (m && m.type === 'video') videos += 1;else if (m) photos += 1;
  }
  return {
    photos,
    videos
  };
}

/* ─── replying to a run or an album: the whole of it, or one item ─────────────
   "When I swipe to reply, let me choose: reply to the message as a whole, or to
    one photo or video — for a run of singles and for an album."
   Both answers are written as an ordinary quote (`replyTo`), so every client that
   already draws quotes draws these, and the tap on the quote jumps where it should:
     · THE WHOLE RUN reads as an album quote ("4 photos", a play badge if any is a
       video) and points at the run's NEWEST item — which is inside the run on
       every phone that grouped it, and the last of them on one that did not.
     · ONE ALBUM ITEM reads as that photo / video and points at the album, with
       `itemIndex` saying which item was meant. */

const isLocalPath = u => typeof u === 'string' && !!u && !u.startsWith('http') && !u.startsWith('data:') && !u.startsWith('pending://');
const isVideoUrl = u => typeof u === 'string' && (u.toLowerCase().includes('.mp4') || u.toLowerCase().includes('.mov'));

/** The message a "reply to all of it" quotes, for a run. */
export function runReplySource(run) {
  const items = runItems(run).filter(Boolean);
  if (!items.length) return null;
  const newest = items[items.length - 1];
  const vid = m => m.type === 'video';
  const localOf = m => isLocalPath(m.localUri) ? m.localUri : null;
  return {
    id: newest.id,
    senderId: newest.senderId,
    senderName: newest.senderName,
    timestamp: newest.timestamp,
    type: 'images',
    // a file on this device makes a sharp quote picture; a shipped blur stands in for one that is not
    images: items.map(m => localOf(m) || m.imageUrl || m.videoUrl || null),
    mediaThumbnails: items.map(m => vid(m) ? m.videoThumbnailUri || null : localOf(m)),
    batchThumbnails: items.map(m => localOf(m) ? null : (vid(m) ? m.videoThumbnailBase64 : m.imageThumbnailBase64) || null),
    _mediaBatch: items.map(m => ({
      kind: vid(m) ? 'video' : 'image'
    })),
    _replyRun: {
      ids: items.map(m => String(m.id)),
      count: items.length,
      hasVideo: items.some(vid)
    }
  };
}

/** The message a reply to ONE item of an album quotes. `isVideo` when the caller knows
    better than the url (the browser reads it from the album's sealed keys). */
export function albumItemReplySource(album, index, isVideo) {
  if (!album || !Number.isInteger(index) || index < 0) return null;
  const imgs = Array.isArray(album.images) ? album.images : [];
  const u = imgs[index];
  const vid = typeof isVideo === 'boolean' ? isVideo : isVideoUrl(u);
  const b64 = Array.isArray(album.batchThumbnails) ? album.batchThumbnails[index] || null : null;
  const still = Array.isArray(album.mediaThumbnails) ? album.mediaThumbnails[index] || null : null;
  /* a sealed album's cloud url is ciphertext — no picture can be made from it, and
     fetching it to try is a wasted download; the shipped blur stands in */
  const sealed = !!(album.batchPerDeviceKeys || album.batchEncryptionKey || album.groupBatchEncryptionData);
  const usable = isLocalPath(u) || !sealed && typeof u === 'string' && !!u && !u.startsWith('pending://');
  return {
    id: album.id,
    senderId: album.senderId,
    senderName: album.senderName,
    timestamp: album.timestamp,
    type: vid ? 'video' : 'image',
    localUri: isLocalPath(u) ? u : null,
    imageUrl: vid || !usable ? null : u,
    videoUrl: vid && usable ? u : null,
    ...(vid ? {
      videoThumbnailBase64: b64,
      videoThumbnailUri: still
    } : {
      imageThumbnailBase64: b64
    }),
    _replyAlbumIndex: index
  };
}

/* ─── selection: a run is selected item by item ─────────────────────────── */

/** 'none' | 'some' | 'all' of a run's items among `selectedIds` */
export function runSelection(run, selectedIds) {
  const items = runItems(run);
  if (!items.length) return 'none';
  const has = id => selectedIds instanceof Set ? selectedIds.has(id) : (selectedIds || []).includes(id);
  const n = items.filter(m => has(String(m.id))).length;
  return n === 0 ? 'none' : n === items.length ? 'all' : 'some';
}

/** Tap on the run itself while selecting: all of it in, or — if all of it already is — all of it out. */
export function toggleRunSelection(run, selected) {
  const items = runItems(run);
  const ids = new Set(items.map(m => String(m.id)));
  const cur = Array.isArray(selected) ? selected : [];
  if (runSelection(run, cur.map(m => String(m.id))) === 'all') {
    return cur.filter(m => !ids.has(String(m.id)));
  }
  const have = new Set(cur.map(m => String(m.id)));
  return [...cur, ...items.filter(m => !have.has(String(m.id)))];
}

/* ═══ ALBUM ITEMS ══════════════════════════════════════════════════════════
   An album (type 'images') is one message. Removing one of its items is a fact
   recorded beside its arrays, never a rewrite of them — the keys, thumbnails,
   qualities, upload states and cloud copies are all addressed by the index the
   item was uploaded under, and they must stay addressable.

     removedImageIndices   deleted for everyone by the sender — nobody sees them
     hiddenItemsFor[uid]   deleted for me — only that account stops seeing them
     deletedImageIndices   (existing) the FILE was cleared from this device —
                           still part of the album, drawn as a "cleared" tile   */

const idxList = v => Array.isArray(v) ? v.map(x => Number(x)).filter(x => Number.isInteger(x) && x >= 0) : [];

/** The album's items this account still sees, by their ORIGINAL index. */
export function albumVisibleIndices(m, myUid) {
  const n = Array.isArray(m?.images) ? m.images.length : 0;
  if (!n) return [];
  const gone = new Set(idxList(m?.removedImageIndices));
  if (myUid && m?.hiddenItemsFor && typeof m.hiddenItemsFor === 'object') {
    for (const i of idxList(m.hiddenItemsFor[myUid])) gone.add(i);
  }
  /* an item the sender cancelled while the album was still uploading is not sent —
     and from the moment it is cancelled, it is not drawn either */
  if (Array.isArray(m?.itemStatus)) m.itemStatus.forEach((st, i) => {
    if (st === 'cancelled') gone.add(i);
  });
  const out = [];
  for (let i = 0; i < n; i += 1) if (!gone.has(i)) out.push(i);
  return out;
}

/** The album's items this account does NOT see (removed / hidden / cancelled), by original index. */
export function albumGoneIndices(m, myUid) {
  const n = Array.isArray(m?.images) ? m.images.length : 0;
  const vis = new Set(albumVisibleIndices(m, myUid));
  const out = [];
  for (let i = 0; i < n; i += 1) if (!vis.has(i)) out.push(i);
  return out;
}

/** True when nothing of the album is left for this account. */
export function albumEmptyFor(m, myUid) {
  return Array.isArray(m?.images) && m.images.length > 0 && albumVisibleIndices(m, myUid).length === 0;
}

/** The fields to write to remove ONE item: 'everyone' (sender only) or 'me'. */
export function albumItemRemoval(m, index, scope, myUid) {
  const i = Number(index);
  if (!Number.isInteger(i) || i < 0) return {};
  if (scope === 'everyone') {
    const cur = idxList(m?.removedImageIndices);
    return {
      removedImageIndices: cur.includes(i) ? cur : [...cur, i].sort((a, b) => a - b)
    };
  }
  const map = m?.hiddenItemsFor && typeof m.hiddenItemsFor === 'object' ? m.hiddenItemsFor : {};
  const cur = idxList(map[myUid]);
  return {
    hiddenItemsFor: {
      ...map,
      [myUid]: cur.includes(i) ? cur : [...cur, i].sort((a, b) => a - b)
    }
  };
}

/** Two copies of the same album meet (local row, cloud doc): removals only ever add up. */
export function mergeAlbumRemovals(a, b) {
  const out = {};
  const r = Array.from(new Set([...idxList(a?.removedImageIndices), ...idxList(b?.removedImageIndices)])).sort((x, y) => x - y);
  if (r.length) out.removedImageIndices = r;
  const ha = a?.hiddenItemsFor && typeof a.hiddenItemsFor === 'object' ? a.hiddenItemsFor : {};
  const hb = b?.hiddenItemsFor && typeof b.hiddenItemsFor === 'object' ? b.hiddenItemsFor : {};
  const uids = new Set([...Object.keys(ha), ...Object.keys(hb)]);
  if (uids.size) {
    const h = {};
    for (const u of uids) h[u] = Array.from(new Set([...idxList(ha[u]), ...idxList(hb[u])])).sort((x, y) => x - y);
    out.hiddenItemsFor = h;
  }
  return out;
}
const pick = (arr, idx) => Array.isArray(arr) ? idx.map(i => arr[i]) : arr;

/**
 * A copy of an album holding only `indices` (original indices, in order) — for
 * forwarding a subset. Every per-item array is narrowed the same way; the key
 * material is left whole (the forward re-wraps the narrowed keys itself) and
 * `_sourceIndices` says which slots of the original the copy carries.
 */
export function narrowAlbum(m, indices) {
  const n = Array.isArray(m?.images) ? m.images.length : 0;
  const idx = (indices || []).map(Number).filter(i => Number.isInteger(i) && i >= 0 && i < n);
  const del = new Set(idxList(m?.deletedImageIndices));
  return {
    ...m,
    images: pick(m.images, idx),
    batchThumbnails: pick(m.batchThumbnails, idx),
    mediaThumbnails: pick(m.mediaThumbnails, idx),
    mediaQualities: pick(m.mediaQualities, idx),
    filterColors: pick(m.filterColors, idx),
    savedTiles: pick(m.savedTiles, idx),
    remoteImages: pick(m.remoteImages, idx),
    deletedImageIndices: idx.map((o, k) => del.has(o) ? k : -1).filter(k => k >= 0),
    removedImageIndices: undefined,
    hiddenItemsFor: undefined,
    _sourceIndices: idx
  };
}

/* ─── uploading: cancel one item ─────────────────────────────────────────── */

/** An item the sender has cancelled while the album was still uploading. */
export const ITEM_CANCELLED = 'cancelled';

/** Is this item still worth an ✕ — not finished, not failed, not already cancelled? */
export function itemCancellable(itemStatus, index) {
  const s = Array.isArray(itemStatus) ? itemStatus[index] : undefined;
  return s !== 'success' && s !== 'failed' && s !== ITEM_CANCELLED;
}

/** After a per-item cancel: is anything of the album left to send? */
export function albumHasLiveItems(itemStatus, count) {
  for (let i = 0; i < count; i += 1) {
    const s = Array.isArray(itemStatus) ? itemStatus[i] : undefined;
    if (s !== ITEM_CANCELLED) return true;
  }
  return false;
}
